import { createContext, useContext, useEffect, useState } from "react";

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
    const [lang, setLang] = useState(() => {
        try {
            return localStorage.getItem("lang") || "it";
        } catch {
            return "it";
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem("lang", lang);
        } catch {
            /* noop */
        }
        document.documentElement.lang = lang;
    }, [lang]);

    return (
        <LanguageContext.Provider value={{ lang, setLang, t: translations[lang] }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    return useContext(LanguageContext);
}

export function validationMessage(detail, t) {
    if (!Array.isArray(detail)) return null;
    const names = [
        ...new Set(
            detail.map((d) => {
                const field = d?.loc?.[d.loc.length - 1];
                return t.validation.fields[field] || field || "?";
            })
        ),
    ];
    return `${t.validation.prefix}${names.join(", ")}`;
}

export const translations = {
    it: {
        nav: {
            home: "Home",
            consulenza: "Consulenza",
            esperienza: "Esperienza",
            progetti: "Progetti",
            contatti: "Contatti",
            cta: "Parliamone",
        },
        hero: {
            title: "CAPRI GIANLUCA",
            subtitle: "Senior ICT Consultant & Project Manager IT · ERP · MES · Business & Process Transformation",
            credentials: "Ingegnere – Iscritto all'Ordine degli Ingegneri della Provincia di Ancona",
            text: "Consulenza strategica e direzione di programmi ICT in contesti industriali anche complessi.",
            text2: "Intervento a supporto del management nella definizione delle strategie applicative, nella trasformazione dei processi e nella governance di programmi ERP, MES e Digital Transformation, con particolare attenzione alla coerenza tra modello organizzativo, processi operativi, sistemi informativi e investimenti tecnologici.",
            experience:
                "Oltre 25 anni di esperienza nei sistemi informativi aziendali, con una specializzazione consolidata nei contesti Manufacturing & Distribution.",
            ctaPrimary: "Parliamone",
            ctaSecondary: "Scopri i servizi",
        },
        consulenza: {
            chapter: "01",
            eyebrow: "Consulenza",
            title: "Consulenza",
            rowLabel: "Ambito",
            items: [
                {
                    number: "01",
                    title: "Project Management & ICT Governance",
                    desc: "Governance dell'ICT e gestione di progetti IT, ERP e MES, con esperienza sia in contesti consulenziali sia all'interno di organizzazioni aziendali. Coordinamento di attività, priorità, budget, stakeholder e fornitori, con presidio dell'evoluzione dei sistemi informativi e dei processi aziendali.",
                },
                {
                    number: "02",
                    title: "Business & Process Analysis",
                    desc: "Analisi dei processi aziendali, requirements gathering e formalizzazione dei requisiti, definizione delle esigenze funzionali, predisposizione di URS e supporto alla progettazione e all'evoluzione delle soluzioni, operando come interfaccia tra business, management, utenti e team tecnici.",
                },
                {
                    number: "03",
                    title: "ERP / MES",
                    desc: "Esperienza pluriennale maturata su sistemi ERP nazionali e internazionali, nell'ambito di progetti di implementazione, evoluzione, integrazione e trasformazione dei sistemi informativi aziendali. Ha lavorato, in particolare, sui seguenti sistemi ERP/MES:",
                    tags: [
                        "Zucchetti Ad Hoc Enterprise / Revolution",
                        "TeamSystem — Gamma Enterprise / Revolution / Alyante",
                        "Microsoft Dynamics 365 — Business Central",
                        "J.D. Edwards OneWorld",
                        "RTS — RTS Suite",
                        "TESAR — MOTIS MES",
                    ],
                },
                {
                    number: "04",
                    title: "Data Management, Integration & Migration",
                    desc: "Consulenza e coordinamento su dati, integrazione e migrazione dei sistemi informativi: analisi dei flussi e delle strutture informative, definizione delle regole di trasformazione, ETL, data quality, migrazioni ERP, integrazione tra ERP, MES, database e applicazioni, reporting e dashboard.",
                },
                {
                    number: "05",
                    title: "Application Solutions",
                    desc: "Analisi e coordinamento di soluzioni applicative a supporto dei processi aziendali, dalla definizione dei requisiti alla valutazione e supervisione delle soluzioni, con particolare riferimento a produzione, pianificazione MPS/MRP, magazzino, commerciale e amministrazione.",
                },
            ],
        },
        sectorGroups: {
            eyebrow: "Settori",
            groups: [
                {
                    title: "Manufacturing & Industry",
                    items: ["Meccanica", "Automotive", "Precision Engineering", "Packaging", "Plastica", "Food & Beverage", "Beauty & Cosmetics", "Elettronica", "Aerospazio", "Furniture"],
                },
                {
                    title: "Distribution & Logistics",
                    items: ["GDO", "Distribuzione", "Retail", "Logistica", "Supply Chain"],
                },
                {
                    title: "Professional & Business Services",
                    items: ["Studi commerciali", "Consulenti del lavoro", "Software & ICT"],
                },
            ],
        },
        problems: {
            chapter: "02",
            eyebrow: "I problemi che risolvo",
            title: "Quando serve governare il progetto",
            items: [
                "Il progetto ERP/MES è in ritardo?",
                "Il rapporto con il fornitore non funziona?",
                "Gli utenti e il team tecnico hanno obiettivi diversi?",
                "La Direzione non ha una visione chiara dello stato del progetto?",
                "Serve temporaneamente un IT Manager senior?",
                "Il progetto deve integrare ERP, MES e altri sistemi?",
            ],
            conclusionA: "Se una di queste domande ti riguarda:",
            conclusionB: "posso entrare nel progetto e assumerne il coordinamento.",
            cta: "Parliamone",
        },
        experience: {
            chapter: "03",
            eyebrow: "Esperienza e competenze",
            title: "Un percorso costruito dal 1998, dal codice alla governance",
            description:
                "Dallo sviluppo software gestionale alla direzione dei sistemi informativi, fino alla fabbrica digitale con i sistemi MES.",
            items: [
                { year: "1998", title: "ERP & Enterprise Systems", desc: "Esperienza pluriennale in progetti ERP nazionali e internazionali, con particolare riferimento a piattaforme enterprise di primaria rilevanza. Gestione di iniziative complesse a supporto dei processi produttivi, MRP, supply chain, magazzino, commerciale, amministrazione, controllo di gestione, budgeting e manutenzione, con integrazione di soluzioni e sistemi di terze parti ad alto valore aggiunto, progettati per estendere le funzionalità dell'ecosistema applicativo e rispondere a specifiche esigenze di business." },
                { year: "2005", title: "ERP Project Management", desc: "Governance e direzione di progetti ERP complessi, con responsabilità su strategia, organizzazione, stakeholder, fornitori e team di progetto, assicurando allineamento tra obiettivi di business, processi aziendali e soluzioni tecnologiche." },
                { year: "2016", title: "IT Management", desc: "Responsabilità dei sistemi informativi aziendali: infrastrutture, sicurezza, applicazioni e budget IT." },
                { year: "2020", title: "Information Systems Management", desc: "Governance dei sistemi informativi e digital transformation dei processi aziendali." },
                { year: "2023", title: "MES Project Management", desc: "Esperienza nella gestione di progetti MES per la digitalizzazione dei processi produttivi, dall'analisi dei requisiti alla progettazione e integrazione con l'ERP, fino al rollout, all'avviamento e alla messa a regime delle soluzioni in ambiente industriale." },
            ],
            ctu: {
                label: "21 anni come",
                title: "Consulente Tecnico d'Ufficio (CTU)",
                text: "Oltre vent'anni di attività per il Tribunale: rigore, metodo e oggettività nell'analisi tecnica dei sistemi informativi.",
            },
            software: {
                title: "Sviluppo software gestionale",
                text: "Esperienza diretta di sviluppo sui processi aziendali:",
                tags: ["Produzione", "MRP", "Magazzino", "Commerciale", "Amministrazione", "Controllo di gestione", "Budget", "Manutenzione"],
            },
        },
        stats: {
            items: [
                { value: "28+", label: "Anni nei sistemi informativi" },
                { value: "1998", label: "Inizio del percorso ERP" },
                { value: "21", label: "Anni come CTU" },
                { value: "ERP · MES", label: "Specializzazione Manufacturing & Distribution" },
            ],
        },
        difference: {
            label: "04 — La differenza",
            headline: "Strategia, processi, tecnologia. Un'unica visione.",
            p1: "Un approccio integrato alla trasformazione aziendale, che unisce competenze ICT, conoscenza dei processi industriali e capacità di governo dei progetti.",
            p2: "Perché una soluzione tecnologica genera valore solo quando è coerente con il processo che deve supportare.",
        },
        mes: {
            chapter: "05",
            eyebrow: "MES",
            title: "Il cuore digitale della fabbrica",
            text: "Dalla definizione dei requisiti all'avviamento in produzione: seguo l'intero ciclo di vita di un sistema MES, facendo dialogare fabbrica e gestionale in un unico flusso informativo.",
            motisLabel: "MOTIS MES · TESAR",
            motisA: "Dal 2023 mi sono specializzato sui sistemi MES lavorando con TESAR sulla piattaforma ",
            motisB: ": raccolta dati di produzione, integrazione con l'ERP e avviamento presso i siti produttivi.",
            items: [
                "Gestione ordini di produzione",
                "Avanzamento e monitoraggio produzione",
                "Gestione strumenti e attrezzature",
                "Ricette e parametri di processo",
                "Manutenzione",
                "Integrazione MES ↔ ERP",
                "URS — User Requirement Specification",
                "Test e collaudo",
                "Rollout",
                "Avviamento e formazione",
            ],
        },
        cases: {
            chapter: "06",
            eyebrow: "Progetti",
            title: "Esperienza professionale",
            description:
                "28 anni di esperienza nella consulenza ICT e nella direzione di progetti complessi, maturati al fianco di aziende industriali italiane e internazionali. Un percorso professionale costruito integrando processi aziendali, sistemi ERP e MES, tecnologia e governance progettuale, con responsabilità crescenti nella definizione delle soluzioni, nella gestione degli stakeholder e nel coordinamento di programmi di trasformazione. La selezione seguente sintetizza alcune delle principali esperienze progettuali. Per ragioni di riservatezza, i riferimenti ai clienti sono omessi; gli approfondimenti possono essere forniti in sede di confronto professionale.",
            contextLabel: "Contesto",
            actionLabel: "Intervento",
            resultsLabel: "Risultati",
            items: [
                {
                    id: "erp-rollout",
                    sector: "Metalmeccanico",
                    title: "Rollout ERP in un gruppo manifatturiero",
                    context: "Tre stabilimenti, processi diversi tra loro e un rapporto con il fornitore in difficoltà.",
                    action: "Governance completa del progetto: piano unico, comitato direzionale, coordinamento del fornitore e degli utenti chiave fino al go-live.",
                    results: [
                        { value: "9", label: "mesi dall'analisi al go-live" },
                        { value: "3", label: "siti produttivi allineati" },
                        { value: "-30%", label: "tempi di inserimento ordini" },
                    ],
                },
                {
                    id: "mes-motis",
                    sector: "Meccanica di precisione",
                    title: "MES MOTIS (TESAR) in produzione",
                    context: "Avanzamento di produzione su carta e dati macchina mai raccolti.",
                    action: "URS, integrazione bidirezionale MES ↔ ERP, test di collaudo, rollout e formazione degli operatori in reparto.",
                    results: [
                        { value: "25", label: "macchine collegate in tempo reale" },
                        { value: "OEE", label: "visibile in tempo reale" },
                        { value: "0", label: "fermi impianto al go-live" },
                    ],
                },
                {
                    id: "project-rescue",
                    sector: "Packaging",
                    title: "Recupero di un progetto ERP bloccato",
                    context: "Progetto fermo da oltre un anno e rapporto con il fornitore compromesso.",
                    action: "Assessment dello stato reale, piano di recupero condiviso e coordinamento di fornitore e utenti fino al go-live.",
                    results: [
                        { value: "3", label: "settimane per l'assessment" },
                        { value: "5", label: "mesi per tornare al go-live" },
                        { value: "1", label: "piano unico Direzione-fornitore" },
                    ],
                },
                {
                    id: "gdo-tentata-vendita",
                    sector: "GDO · Tentata vendita",
                    title: "Fusione di due distributori in un unico ERP",
                    context: "Migrazione ERP per la fusione di due società distributrici, con reingegnerizzazione completa del sistema di tentata vendita.",
                    action: "Progettazione della nuova architettura dati e della procedura di tentata vendita, implementazione dei flussi di ricevimento documenti da 130 VAN e ottimizzazione dei flussi ERP.",
                    results: [
                        { value: "130", label: "VAN collegati con invio documenti" },
                        { value: "2→1", label: "società fuse in un unico sistema" },
                        { value: "100%", label: "flussi di vendita digitalizzati" },
                    ],
                },
                {
                    id: "cantina-vini",
                    sector: "Cantina vinicola",
                    title: "ERP, MPS e MRP multi-plant per il vino",
                    context: "Migrazione ERP con attivazione di MPS e MRP multi-plant e gestione delle campagne vitivinicole.",
                    action: "Assessment e business analysis, migrazione dati e progettazione della nuova architettura MRP su tre depositi, con flussi ottimizzati sulle tre linee di imbottigliamento.",
                    results: [
                        { value: "3", label: "depositi nella nuova architettura MRP" },
                        { value: "3", label: "linee di imbottigliamento ottimizzate" },
                        { value: "MPS", label: "pianificazione multi-plant attiva" },
                    ],
                },
                {
                    id: "refrigeratori",
                    sector: "Metalmeccanica · Refrigeratori d'acqua",
                    title: "Budget vendite integrato nell'ERP",
                    context: "Migrazione ERP con implementazione di un nuovo modulo per la gestione del budget delle vendite.",
                    action: "Attività ETL per la migrazione dati, formazione e test pre-go-live. Go-live con ordini di produzione di saldatura gestiti a commessa e dati di budget ad alimentare il PPP (Piano Principale di Produzione).",
                    results: [
                        { value: "ETL", label: "migrazione dati completata" },
                        { value: "PPP", label: "alimentato dal budget vendite" },
                        { value: "1", label: "gestione commessa unificata" },
                    ],
                },
            ],
        },
        international: {
            chapter: "07",
            eyebrow: "Esperienza internazionale",
            title: "Progetti oltre confine",
            statement:
                "Progetti ERP, Manufacturing e MES, inclusi avviamenti presso siti produttivi e collaborazione con team internazionali.",
            countries: [
                { code: "NL", name: "Paesi Bassi" },
                { code: "DK", name: "Danimarca" },
                { code: "SI", name: "Slovenia" },
                { code: "BA", name: "Bosnia ed Erzegovina" },
            ],
        },
        contact: {
            chapter: "08",
            eyebrow: "Contatti",
            title: "Hai un progetto ERP, MES o IT da governare?",
            description:
                "Raccontami brevemente la situazione. Una prima conversazione di 20 minuti è sufficiente per capire se posso essere utile.",
            direct: "Preferisci parlarne direttamente?",
            phoneLabel: "Telefono",
            callLabel: "Chiama",
            whatsappLabel: "WhatsApp",
            whatsappText: "Scrivimi su WhatsApp",
            emailLabel: "Oppure scrivimi direttamente",
            addressLabel: "Dove sono",
            form: {
                name: "Nome e Cognome *",
                namePlaceholder: "Mario Rossi",
                company: "Azienda *",
                companyPlaceholder: "Azienda S.p.A.",
                email: "Email *",
                emailPlaceholder: "mario.rossi@azienda.it",
                phone: "Telefono",
                need: "Di cosa hai bisogno? *",
                selectPlaceholder: "Seleziona…",
                message: "Raccontami brevemente la situazione *",
                messagePlaceholder: "Il progetto ERP è partito 6 mesi fa ma…",
                privacyA: "Ho letto e accetto l'",
                privacyLink: "informativa privacy",
                privacyB: " per il trattamento dei dati personali. *",
                submit: "Invia richiesta",
                sending: "Invio in corso…",
                errorPrivacy: "Devi accettare l'informativa privacy per inviare la richiesta.",
                errorFields: "Controlla i campi evidenziati e riprova.",
                errorGeneric: "Invio non riuscito. Riprova o scrivimi direttamente via email.",
                toastTitle: "Richiesta inviata",
                toastDesc: "Grazie! Ti ricontatterò al più presto per una prima conversazione di 20 minuti.",
            },
            needs: [
                "Progetto ERP",
                "Progetto MES",
                "Progetto IT in difficoltà",
                "Coordinamento fornitori",
                "IT Management temporaneo",
                "Assessment / verifica situazione IT",
                "Altro",
            ],
        },
        contactPage: {
            eyebrow: "Parliamone",
            title: "Contatti",
            name: "Ing. Capri Gianluca",
            role: "Senior IT, ERP & MES Consultant & Project Manager",
            sedeLabel: "Sede",
            pivaLabel: "P. IVA",
            phoneLabel: "Telefono",
            emailLabel: "Email",
            webLabel: "Sito Web",
            whatsappCta: "Scrivimi su WhatsApp",
            formTitle: "Scrivimi un messaggio",
            nameField: "Nome *",
            surnameField: "Cognome *",
            namePlaceholder: "Mario",
            surnamePlaceholder: "Rossi",
            emailField: "Email *",
            emailPlaceholder: "mario.rossi@azienda.it",
            phoneField: "Telefono",
            messageField: "Messaggio *",
            messagePlaceholder: "Raccontami brevemente la tua esigenza…",
            privacyA: "Ho letto e accetto l'",
            privacyLink: "informativa privacy",
            privacyB: " per il trattamento dei dati personali. *",
            submit: "Invia messaggio",
            sending: "Invio in corso…",
            errorPrivacy: "Devi accettare l'informativa privacy per inviare il messaggio.",
            errorFields: "Controlla i campi e riprova.",
            errorGeneric: "Invio non riuscito. Riprova o scrivimi direttamente via email.",
            toastTitle: "Messaggio inviato",
            toastDesc: "Grazie! Ti ricontatterò al più presto.",
            back: "Torna al sito",
            mapLabel: "Dove sono",
        },
        footer: {
            tagline: "ERP · MES · Digital Transformation · IT Governance",
            rights: "© 2026 Gianluca Capri — Tutti i diritti riservati",
            privacy: "Informativa Privacy",
            talk: "Parliamone",
            contactPage: "Pagina contatti",
        },
        validation: {
            prefix: "Controlla i campi: ",
            fields: {
                nome: "Nome",
                cognome: "Cognome",
                azienda: "Azienda",
                email: "Email (formato non valido)",
                telefono: "Telefono",
                esigenza: "Di cosa hai bisogno",
                messaggio: "Messaggio (minimo 10 caratteri)",
                privacy: "Privacy",
            },
        },
    },
    en: {
        nav: {
            home: "Home",
            consulenza: "Consulting",
            esperienza: "Experience",
            progetti: "Projects",
            contatti: "Contact",
            cta: "Let's talk",
        },
        hero: {
            title: "CAPRI GIANLUCA",
            subtitle: "Senior ICT Consultant & Project Manager IT · ERP · MES · Business & Process Transformation",
            credentials: "Engineer – Registered with the Order of Engineers of the Province of Ancona",
            text: "Strategic consulting and direction of ICT programs in industrial contexts, including complex ones.",
            text2: "Supporting management in defining application strategies, transforming processes and governing ERP, MES and Digital Transformation programs, with particular attention to coherence between organizational model, operational processes, information systems and technology investments.",
            experience:
                "Over 25 years of experience in corporate information systems, with consolidated specialization in Manufacturing & Distribution.",
            ctaPrimary: "Let's talk",
            ctaSecondary: "Explore the services",
        },
        consulenza: {
            chapter: "01",
            eyebrow: "Consulting",
            title: "Consulting",
            rowLabel: "Area",
            items: [
                {
                    number: "01",
                    title: "Project Management & ICT Governance",
                    desc: "ICT governance and management of IT, ERP and MES projects, with experience both in consulting contexts and within corporate organizations. Coordination of activities, priorities, budget, stakeholders and vendors, overseeing the evolution of information systems and business processes.",
                },
                {
                    number: "02",
                    title: "Business & Process Analysis",
                    desc: "Analysis of business processes, requirements gathering and formalization, definition of functional needs, preparation of URS and support for solution design and evolution, acting as the interface between business, management, users and technical teams.",
                },
                {
                    number: "03",
                    title: "ERP / MES",
                    desc: "Long-standing experience on national and international ERP systems, within implementation, evolution, integration and transformation projects of corporate information systems. He has worked, in particular, on the following ERP/MES systems:",
                    tags: [
                        "Zucchetti Ad Hoc Enterprise / Revolution",
                        "TeamSystem — Gamma Enterprise / Revolution / Alyante",
                        "Microsoft Dynamics 365 — Business Central",
                        "J.D. Edwards OneWorld",
                        "RTS — RTS Suite",
                        "TESAR — MOTIS MES",
                    ],
                },
                {
                    number: "04",
                    title: "Data Management, Integration & Migration",
                    desc: "Consulting and coordination on data, integration and migration of information systems: analysis of information flows and structures, definition of transformation rules, ETL, data quality, ERP migrations, integration between ERP, MES, databases and applications, reporting and dashboards.",
                },
                {
                    number: "05",
                    title: "Application Solutions",
                    desc: "Analysis and coordination of application solutions supporting business processes, from requirements definition to solution evaluation and supervision, with particular reference to production, MPS/MRP planning, warehouse, sales and administration.",
                },
            ],
        },
        sectorGroups: {
            eyebrow: "Industries",
            groups: [
                {
                    title: "Manufacturing & Industry",
                    items: ["Mechanical Engineering", "Automotive", "Precision Engineering", "Packaging", "Plastics", "Food & Beverage", "Beauty & Cosmetics", "Electronics", "Aerospace", "Furniture"],
                },
                {
                    title: "Distribution & Logistics",
                    items: ["Large-Scale Retail", "Distribution", "Retail", "Logistics", "Supply Chain"],
                },
                {
                    title: "Professional & Business Services",
                    items: ["Accounting Firms", "Employment Consultants", "Software & ICT"],
                },
            ],
        },
        problems: {
            chapter: "02",
            eyebrow: "The problems I solve",
            title: "When a project needs governance",
            items: [
                "Is the ERP/MES project behind schedule?",
                "Is the relationship with the vendor not working?",
                "Do users and the technical team have different goals?",
                "Does management lack a clear view of the project status?",
                "Do you temporarily need a senior IT Manager?",
                "Does the project need to integrate ERP, MES and other systems?",
            ],
            conclusionA: "If any of these questions sounds familiar:",
            conclusionB: "I can step into the project and take over its coordination.",
            cta: "Let's talk",
        },
        experience: {
            chapter: "03",
            eyebrow: "Experience and skills",
            title: "A journey built since 1998, from code to governance",
            description:
                "From management software development to information systems leadership, up to the digital factory with MES systems.",
            items: [
                { year: "1998", title: "ERP & Enterprise Systems", desc: "Multi-year experience in national and international ERP projects, with particular reference to leading enterprise platforms. Management of complex initiatives supporting production, MRP, supply chain, warehouse, sales, accounting, management control, budgeting and maintenance processes, integrating high-value third-party solutions designed to extend the application ecosystem and meet specific business needs." },
                { year: "2005", title: "ERP Project Management", desc: "Governance and direction of complex ERP projects, with responsibility for strategy, organization, stakeholders, vendors and project teams, ensuring alignment between business goals, company processes and technological solutions." },
                { year: "2016", title: "IT Management", desc: "Responsibility for corporate information systems: infrastructure, security, applications and IT budget." },
                { year: "2020", title: "Information Systems Management", desc: "Governance of information systems and digital transformation of business processes." },
                { year: "2023", title: "MES Project Management", desc: "Experience in managing MES projects for the digitalization of production processes, from requirements analysis to design and ERP integration, through rollout, start-up and ramp-up of solutions in industrial environments." },
            ],
            ctu: {
                label: "21 years as",
                title: "Court-Appointed Technical Consultant (CTU)",
                text: "Over twenty years serving the Court: rigour, method and objectivity in the technical analysis of information systems.",
            },
            software: {
                title: "Management software development",
                text: "Hands-on development experience across business processes:",
                tags: ["Production", "MRP", "Warehouse", "Sales", "Accounting", "Management control", "Budgeting", "Maintenance"],
            },
        },
        stats: {
            items: [
                { value: "28+", label: "Years in information systems" },
                { value: "1998", label: "Start of the ERP journey" },
                { value: "21", label: "Years as court-appointed consultant" },
                { value: "ERP · MES", label: "Specialization in Manufacturing & Distribution" },
            ],
        },
        difference: {
            label: "04 — The difference",
            headline: "Strategy, processes, technology. A single vision.",
            p1: "An integrated approach to business transformation, combining ICT skills, knowledge of industrial processes and project governance capabilities.",
            p2: "Because a technological solution generates value only when it is consistent with the process it must support.",
        },
        mes: {
            chapter: "05",
            eyebrow: "MES",
            title: "The digital heart of the factory",
            text: "From requirements definition to production start-up: I follow the entire lifecycle of an MES system, connecting factory and ERP in a single information flow.",
            motisLabel: "MOTIS MES · TESAR",
            motisA: "Since 2023 I have specialized in MES systems working with TESAR on the ",
            motisB: " platform: production data collection, ERP integration and start-up at production sites.",
            items: [
                "Production order management",
                "Production progress and monitoring",
                "Tools and equipment management",
                "Recipes and process parameters",
                "Maintenance",
                "MES ↔ ERP integration",
                "URS — User Requirement Specification",
                "Testing and acceptance",
                "Rollout",
                "Start-up and training",
            ],
        },
        cases: {
            chapter: "06",
            eyebrow: "Projects",
            title: "Professional experience",
            description:
                "28 years of experience in ICT consulting and in directing complex projects, built alongside Italian and international industrial companies. A professional journey integrating business processes, ERP and MES systems, technology and project governance, with growing responsibility in solution definition, stakeholder management and the coordination of transformation programs. The following selection summarizes some of the main project experiences. For confidentiality reasons, client references are omitted; details can be provided during a professional discussion.",
            contextLabel: "Context",
            actionLabel: "Intervention",
            resultsLabel: "Results",
            items: [
                {
                    id: "erp-rollout",
                    sector: "Metalworking",
                    title: "ERP rollout in a manufacturing group",
                    context: "Three plants, different processes and a struggling vendor relationship.",
                    action: "Full project governance: a single plan, steering committee, coordination of the vendor and key users through go-live.",
                    results: [
                        { value: "9", label: "months from analysis to go-live" },
                        { value: "3", label: "production sites aligned" },
                        { value: "-30%", label: "order entry time" },
                    ],
                },
                {
                    id: "mes-motis",
                    sector: "Precision mechanics",
                    title: "MOTIS MES (TESAR) in production",
                    context: "Paper-based production tracking and machine data never collected.",
                    action: "URS, bidirectional MES ↔ ERP integration, acceptance testing, rollout and operator training on the shop floor.",
                    results: [
                        { value: "25", label: "machines connected in real time" },
                        { value: "OEE", label: "visible in real time" },
                        { value: "0", label: "downtime at go-live" },
                    ],
                },
                {
                    id: "project-rescue",
                    sector: "Packaging",
                    title: "Rescuing a stalled ERP project",
                    context: "Project stalled for over a year, vendor relationship compromised.",
                    action: "Assessment of the real status, a shared recovery plan and coordination of vendor and users through go-live.",
                    results: [
                        { value: "3", label: "weeks for the assessment" },
                        { value: "5", label: "months back to go-live" },
                        { value: "1", label: "single management-vendor plan" },
                    ],
                },
                {
                    id: "gdo-tentata-vendita",
                    sector: "Retail distribution · Van sales",
                    title: "Merging two distributors into one ERP",
                    context: "ERP migration for the merger of two distribution companies, with complete reengineering of the van-sales system.",
                    action: "Design of the new data architecture and van-sales procedure, implementation of document intake flows from 130 vans and optimization of ERP flows.",
                    results: [
                        { value: "130", label: "vans sending documents" },
                        { value: "2→1", label: "companies merged into one system" },
                        { value: "100%", label: "sales flows digitalized" },
                    ],
                },
                {
                    id: "cantina-vini",
                    sector: "Winery",
                    title: "ERP, MPS and multi-plant MRP for wine",
                    context: "ERP migration with MPS and multi-plant MRP activation and management of the wine-growing campaigns.",
                    action: "Assessment and business analysis, data migration and design of the new MRP architecture across three warehouses, with optimized flows on the three bottling lines.",
                    results: [
                        { value: "3", label: "warehouses in the new MRP architecture" },
                        { value: "3", label: "bottling lines optimized" },
                        { value: "MPS", label: "multi-plant planning active" },
                    ],
                },
                {
                    id: "refrigeratori",
                    sector: "Metalworking · Water coolers",
                    title: "Sales budget integrated into the ERP",
                    context: "ERP migration with implementation of a new sales budget management module.",
                    action: "ETL activities for data migration, training and pre-go-live testing. Go-live with welding production orders managed by job order and budget data feeding the MPS (Master Production Schedule).",
                    results: [
                        { value: "ETL", label: "data migration completed" },
                        { value: "MPS", label: "fed by the sales budget" },
                        { value: "1", label: "unified job-order management" },
                    ],
                },
            ],
        },
        international: {
            chapter: "07",
            eyebrow: "International experience",
            title: "Projects beyond borders",
            statement:
                "ERP, Manufacturing and MES projects, including start-ups at production sites and collaboration with international teams.",
            countries: [
                { code: "NL", name: "Netherlands" },
                { code: "DK", name: "Denmark" },
                { code: "SI", name: "Slovenia" },
                { code: "BA", name: "Bosnia and Herzegovina" },
            ],
        },
        contact: {
            chapter: "08",
            eyebrow: "Contact",
            title: "Have an ERP, MES or IT project to govern?",
            description:
                "Briefly tell me about the situation. A first 20-minute conversation is enough to understand if I can be useful.",
            direct: "Prefer to talk directly?",
            phoneLabel: "Phone",
            callLabel: "Call",
            whatsappLabel: "WhatsApp",
            whatsappText: "Message me on WhatsApp",
            emailLabel: "Or write to me directly",
            addressLabel: "Where I am",
            form: {
                name: "Full name *",
                namePlaceholder: "John Smith",
                company: "Company *",
                companyPlaceholder: "Company Inc.",
                email: "Email *",
                emailPlaceholder: "john.smith@company.com",
                phone: "Phone",
                need: "What do you need? *",
                selectPlaceholder: "Select…",
                message: "Briefly describe the situation *",
                messagePlaceholder: "The ERP project started 6 months ago but…",
                privacyA: "I have read and accept the ",
                privacyLink: "privacy policy",
                privacyB: " for the processing of personal data. *",
                submit: "Send request",
                sending: "Sending…",
                errorPrivacy: "You must accept the privacy policy to send the request.",
                errorFields: "Please check the highlighted fields and try again.",
                errorGeneric: "Sending failed. Please try again or write to me directly via email.",
                toastTitle: "Request sent",
                toastDesc: "Thank you! I'll get back to you shortly for a first 20-minute conversation.",
            },
            needs: [
                "ERP project",
                "MES project",
                "IT project in difficulty",
                "Vendor coordination",
                "Temporary IT management",
                "Assessment / IT situation review",
                "Other",
            ],
        },
        contactPage: {
            eyebrow: "Let's talk",
            title: "Contact",
            name: "Ing. Capri Gianluca",
            role: "Senior IT, ERP & MES Consultant & Project Manager",
            sedeLabel: "Office",
            pivaLabel: "VAT No.",
            phoneLabel: "Phone",
            emailLabel: "Email",
            webLabel: "Website",
            whatsappCta: "Message me on WhatsApp",
            formTitle: "Send me a message",
            nameField: "First name *",
            surnameField: "Last name *",
            namePlaceholder: "John",
            surnamePlaceholder: "Smith",
            emailField: "Email *",
            emailPlaceholder: "john.smith@company.com",
            phoneField: "Phone",
            messageField: "Message *",
            messagePlaceholder: "Briefly describe your needs…",
            privacyA: "I have read and accept the ",
            privacyLink: "privacy policy",
            privacyB: " for the processing of personal data. *",
            submit: "Send message",
            sending: "Sending…",
            errorPrivacy: "You must accept the privacy policy to send the message.",
            errorFields: "Please check the fields and try again.",
            errorGeneric: "Sending failed. Please try again or write to me directly via email.",
            toastTitle: "Message sent",
            toastDesc: "Thank you! I'll get back to you shortly.",
            back: "Back to the site",
            mapLabel: "Where I am",
        },
        footer: {
            tagline: "ERP · MES · Digital Transformation · IT Governance",
            rights: "© 2026 Gianluca Capri — All rights reserved",
            privacy: "Privacy Policy",
            talk: "Let's talk",
            contactPage: "Contact page",
        },
        validation: {
            prefix: "Please check the fields: ",
            fields: {
                nome: "First name",
                cognome: "Last name",
                azienda: "Company",
                email: "Email (invalid format)",
                telefono: "Phone",
                esigenza: "What do you need",
                messaggio: "Message (at least 10 characters)",
                privacy: "Privacy",
            },
        },
    },
};
